<template>
  <div
    ref="designer"
    id="sb_fotowand-designer"
    class="sb_fotowand-designer"
    :class="{ v_inline: inline }"
    :style="{ height: designerHeight }"
  >
    <Symbols />
    <div id="sb_fotowand-designer_container" :class="{ 's_sidebar-open': sidebar, 's_toolbar-open': toolbar }">
      <TopToolbar id="sb_fotowand-designer_toolbar" :toolbar.sync="toolbar" />
      <Canvas
        :loading.sync="loading"
        @close="sidebar = null"
        id="sb_fotowand-designer_canvas"
        @openBackgrounds="sidebar = 'background'"
        :height="designerHeight"
      />
      <Footer
        :loading="loading"
        ref="footer"
        id="sb_fotowand-designer_footer"
        :active.sync="sidebar"
        :setToolbar="setToolbar"
      />
      <Sidebar @addBackground="addBackground" :loading.sync="loading" ref="sidebar" :active.sync="sidebar" />
      <transition name="fade">
        <Modal v-if="modalActive" />
      </transition>
    </div>
  </div>
</template>

<script>
import Symbols from '@/components/SvgSymbols';
import TopToolbar from '@/components/TopToolbar/Toolbar.vue';
import Canvas from '@/components/Canvas/Canvas.vue';
import Footer from '@/components/Footer/Footer.vue';
import Sidebar from '@/components/Sidebar/Sidebar.vue';
import Modal from '@/components/Modal/Modal.vue';
import { mapState, mapMutations } from 'vuex';

export default {
  components: {
    TopToolbar,
    Canvas,
    Footer,
    Sidebar,
    Symbols,
    Modal,
  },
  props: {
    inline: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      sidebar: null,
      toolbar: true,
      intersectionObserver: undefined,
      isObserved: false,
      startOnboardingTimeout: undefined,
    };
  },
  methods: {
    ...mapMutations('canvas', ['loadDesign', 'addImage']),
    ...mapMutations('onboarding', ['startOnboarding', 'stopOnboarding']),
    stopLoading() {
      this.loading = false;
    },
    addBackground() {
      this.handleOnboarding(400);
    },
    setToolbar(state) {
      this.toolbar = state;
    },
    handleOnboarding(delay) {
      if (!this.onboardingCompleted() && this.isObserved && this.hasContent) {
        this.toolbar = true;
        this.startOnboardingTimeout = setTimeout(() => {
          this.startOnboarding();
        }, delay);
      } else if (!this.isObserved) {
        this.stopOnboarding();

        if (this.startOnboardingTimeout) {
          clearTimeout(this.startOnboardingTimeout);
        }
      }
    },
  },
  computed: {
    ...mapState('canvas', ['manager']),
    ...mapState('design', {
      hasContent: (state) => !!state.background || state.design.layers.length !== 0 || state.images.length !== 0,
      design: (state) => state,
    }),
    ...mapState('modal', {
      modalActive: (state) => state.active,
    }),
    ...mapState('onboarding', {
      onboardingCompleted: (state) => state.getCompletedState,
    }),
    designerHeight() {
      if (!this.height) {
        return null;
      }

      const unit = this.height.slice(-2);
      //has a unit
      if (isNaN(parseInt(unit))) {
        return this.height;
      }

      //add the unit
      return `${this.height}px`;
    },
  },
  mounted() {
    if (this.design.id) {
      this.loadDesign(this.design);
    }

    if (!this.onboardingCompleted()) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isObserved = entry.isIntersecting;
            this.handleOnboarding(1000);
          });
        },
        {
          rootMargin: '0px',
          treshold: 1.0,
        },
      );
      this.intersectionObserver.observe(this.$refs.designer);
    }
  },
  unmounted() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },
};
</script>

<style lang="scss" src="@/styles/app.scss"></style>
<style lang="scss" src="@/styles/_shepherd.scss"></style>

<style lang="scss">
#sb_fotowand-designer {
  @import '~@/styles/global';
  @import '~@/styles/_transitions';

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>

<style lang="scss">
#sb_fotowand-designer {
  font-size: 16px;
  color: $brand-gray;
  display: flex;
  left: 0;
  top: 0;
  position: fixed;
  z-index: 0;
  width: 100%;
  height: 100%;
  border-top: 4px solid $brand-lightest-gray;
  border-bottom: 4px solid $brand-primary;
  font-family: ManropeGX, Manrope, serif !important;
  font-weight: 500;
  &.v_inline {
    position: relative;
  }
  &_toolbar,
  &_canvas {
    @include media('<=medium-portrait') {
      transition: transform 0.4s;
    }
  }
  &_container {
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    &.s_sidebar-open {
      .sb_designer_canvas {
        @include media('<=medium-portrait') {
          transform: translateY(-8rem);
        }
        @include media('<=small') {
          transform: translateY(-25%);
        }
        @include media('<=tiny') {
          transform: translateY(-25%) translateY(-1rem);
        }
      }
    }
  }
}
</style>
